// Maps MotorClaimForm's local wizard state (S) to the ClaimBodyDto sections
// the backend's ClaimStagingMapper.applyBody understands. Each to*Body below
// builds one section; toFullClaimBody composes all of them into the single
// payload sent once, at review, to POST /motor-assessment/api/claims/submit
// — the wizard no longer submits per-screen (see the deprecated /claims/step).
// The backend merges each nested section as a whole object — a non-empty
// incoming section REPLACES the current one entirely, not field-by-field —
// so each function still builds the FULL current value of its section(s)
// from S, never a delta.
import { str, list } from '../services/claim-form-mapper'

const CONSENT_STATEMENT =
  'I confirm the information above is true and complete to the best of my knowledge, and I consent to APA verifying it.'

// S.surface mixes two backend concepts in one chip select — split by value
// rather than adding a second UI field.
const WET_DRY_VALUES = new Set(['Dry', 'Wet'])

function splitWhen(when) {
  if (!when) return { date: null, time: null }
  const [date, time] = when.split('T')
  return { date: date || null, time: time || null }
}

export function toWhatHappenedBody(S) {
  const { date, time } = splitWhen(S.when)
  const locationName = str(S.incidentLocation?.locationName)

  const body = {
    accident: {
      date,
      time,
      place: locationName,
      roadSurfaceType: WET_DRY_VALUES.has(S.surface) ? null : str(S.surface),
      surfaceWetOrDry: WET_DRY_VALUES.has(S.surface) ? S.surface : null,
      visibility: str(S.vis),
      lightsShowing: str(S.light),
      weatherConditions: str(S.weather),
      // S.police is already boolean|null (SegmentedControl), not the 'Yes'/
      // 'No' strings bool() normalises — pass through as-is.
      policeTookParticulars: S.police,
      constableNo: str(S.ob),
      reportedPoliceStation: str(S.station),
      policeTel: S.police === true ? str(S.policePhone) : null,
      trafficOffenceNo: S.police === true ? str(S.trafficOffenceNumber) : null,
    },
    incidentLocation: {
      // ?? (not ||) since 0 is a valid latitude/longitude (equator / prime
      // meridian) and must not be coerced to null.
      latitude: S.incidentLocation?.latitude ?? null,
      longitude: S.incidentLocation?.longitude ?? null,
      locationName,
    },
    driverStatement: str(S.story),
  }

  if (S.driverWasMe === false) {
    const driver = {}
    if (str(S.otherDriverName)) driver.name = str(S.otherDriverName)
    if (str(S.otherDriverEmail)) driver.email = str(S.otherDriverEmail)
    if (typeof S.driverToBlame === 'boolean') driver.toBlame = S.driverToBlame
    if (Object.keys(driver).length) body.driver = driver
  }

  return body
}

export function toOtherVehiclesBody(S) {
  const vehicles = (S.vehicles || []).map((v) => ({
    regNo: str(v.reg),
    ownerNameAddress: str([v.owner, v.phone].filter(Boolean).join(' — ')),
    insurer: str(v.insurer),
    otherPropertyDamaged: str(v.damage),
    driverNameAddress: null,
  }))

  const otherPropertyNote = str(S.otherProperty)
  if (otherPropertyNote && vehicles.length) {
    vehicles[0].otherPropertyDamaged = str(
      [vehicles[0].otherPropertyDamaged, otherPropertyNote]
        .filter(Boolean)
        .join('; ')
    )
  }

  return { otherVehicles: vehicles }
}

export function toPeopleBody(S) {
  const injuredPersons = []
  const passengers = []

  ;(S.people || []).forEach((p) => {
    const isInjured = p.injured && p.injured !== 'Not injured'
    const isPassenger = (p.role || '').startsWith('Passenger')

    if (isInjured) {
      injuredPersons.push({
        nameAddress: str(p.name),
        relationshipToInsured: str(p.role),
        driverOrPassenger: (p.role || '').toLowerCase().includes('driver')
          ? 'Driver'
          : 'Passenger',
        regNo: null,
        apparentInjuries: str(
          [p.injured, p.hospital].filter(Boolean).join(' — ')
        ),
      })
    }

    if (isPassenger) {
      passengers.push({ name: str(p.name), address: null })
    }
  })

  const witnesses = str(S.witnessName)
    ? list({
        name: str(S.witnessName),
        // ClaimWitness has no phone field — folded into address as a labelled
        // stand-in rather than dropping the contact info entirely.
        address: str(S.witnessPhone) ? `Phone: ${S.witnessPhone}` : null,
      })
    : []

  return { injuredPersons, witnesses, passengers }
}

export function toDamageBody(S) {
  const parts = []
  if (str(S.severity)) parts.push(`${S.severity} damage`)
  if (S.areas?.length) parts.push(`Areas: ${S.areas.join(', ')}`)
  if (str(S.preex)) parts.push(`Pre-existing damage: ${S.preex}`)
  if (str(S.damageDesc)) parts.push(S.damageDesc)

  return {
    damage: {
      apparentDamage: str(parts.join('. ')),
      repairerNameAddress: null,
      repairerTel: null,
      vehicleStillInUse: null,
      inspectionWhenWhere: str(
        S.assess === 'a1' ? 'APA Assessment Centre' : S.assessAddr
      ),
    },
  }
}

export function toDocumentsBody(S) {
  return {
    attachments: Object.values(S.docs || {})
      .filter((d) => d?.url)
      .map((d) => ({ type: d.type, url: d.url })),
  }
}

export function toReviewBody(S) {
  const body = {
    ownerInsuredStatement: S.consent ? CONSENT_STATEMENT : null,
    declarationDate: new Date().toISOString().slice(0, 10),
    insuredSigned: !!S.consent,
  }

  // "Someone else" was already sent from step 2 (S.otherDriverName); "Me"
  // can only resolve once the signature is typed, which happens on this
  // same screen just before this call.
  if (S.driverWasMe !== false) {
    const name = str(S.sign)
    if (name) body.driver = { name }
    if (typeof S.driverToBlame === 'boolean') {
      body.driver = { ...body.driver, toBlame: S.driverToBlame }
    }
  }

  return body
}

// Composes every section into the one body sent to /claims/submit. The
// individual to*Body functions never write the same top-level key — driver
// is the only field two of them can set, and they're mutually exclusive on
// S.driverWasMe — so a plain merge is safe; nothing here needs the backend's
// section-replaces-whole-object merge semantics to reconcile a conflict.
export function toFullClaimBody(S, origin) {
  return {
    ...toWhatHappenedBody(S),
    ...toOtherVehiclesBody(S),
    ...toPeopleBody(S),
    ...toDamageBody(S),
    ...toDocumentsBody(S),
    ...toReviewBody(S),
    ...(origin ? { origin } : {}),
  }
}
